<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <!-- Header -->
      <div class="d-flex align-center mb-5">
        <span class="title-text">แดชบอร์ด</span>
        <v-spacer />
        <v-chip small outlined color="#6b717f" class="mr-2">
          <v-icon small left>mdi-calendar</v-icon>
          {{ todayLabel }}
        </v-chip>
        <v-btn icon small @click="refreshData" :loading="isRefreshing">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Stat Cards -->
      <v-row class="mb-2">
        <v-col
          v-for="card in statCards"
          :key="card.key"
          cols="12"
          sm="6"
          lg="4"
          xl
        >
          <v-card class="stat-card pa-4" elevation="0">
            <div class="d-flex align-center">
              <v-avatar :color="card.bgColor" size="48" class="mr-4">
                <v-icon :color="card.iconColor" size="24">{{
                  card.icon
                }}</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="stat-label">{{ card.label }}</div>
                <div class="stat-value">{{ card.value }}</div>
              </div>
            </div>
            <div v-if="card.sub" class="stat-sub mt-2">{{ card.sub }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Top 10 Best Selling Products -->
        <v-col cols="12" lg="7">
          <v-card class="card-container pa-0" elevation="0">
            <v-card-title class="px-5 pt-5 pb-2">
              <div class="d-flex align-center" style="width: 100%">
                <v-icon color="#F4811F" class="mr-2">mdi-trophy</v-icon>
                <span class="section-title">สินค้าขายดี 10 อันดับ</span>
                <v-spacer />
                <v-chip small color="#FFF3E6" text-color="#F4811F">
                  ทั้งระบบ
                </v-chip>
              </div>
            </v-card-title>

            <v-card-text class="px-5 pb-5">
              <div v-if="topProducts.length === 0" class="text-center py-10">
                <img
                  src="@/assets/not_exist_search.svg"
                  alt="no-data"
                  width="80"
                  height="80"
                />
                <div class="no-data-text mt-3">ยังไม่มีข้อมูลการขาย</div>
              </div>

              <v-simple-table v-else class="top-product-table">
                <thead>
                  <tr>
                    <th style="width: 50px">#</th>
                    <th>สินค้า</th>
                    <th class="text-center">ขายได้</th>
                    <th class="text-right">ยอดขาย</th>
                    <th class="text-center hidden-sm-and-down">สต็อก</th>
                    <th class="hidden-sm-and-down">ร้านค้า</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in topProducts" :key="item.productId">
                    <td>
                      <v-avatar
                        :color="getRankColor(item.rank)"
                        size="28"
                        class="rank-badge"
                      >
                        <span class="white--text font-weight-bold text-caption">
                          {{ item.rank }}
                        </span>
                      </v-avatar>
                    </td>
                    <td>
                      <div class="d-flex align-center py-2">
                        <v-avatar size="40" rounded class="mr-3">
                          <v-img
                            v-if="item.image"
                            :src="item.image"
                            :alt="item.productName"
                          />
                          <v-icon v-else color="grey lighten-1"
                            >mdi-image-off</v-icon
                          >
                        </v-avatar>
                        <div class="product-name-cell">
                          <div class="product-name">
                            {{ item.productName }}
                          </div>
                          <div
                            v-if="item.category"
                            class="product-category"
                          >
                            {{ item.category.name }}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-center font-weight-medium">
                      {{ formatNumber(item.totalSold) }} ชิ้น
                    </td>
                    <td class="text-right font-weight-medium">
                      ฿{{ formatCurrency(item.totalRevenue) }}
                    </td>
                    <td class="text-center hidden-sm-and-down">
                      <v-chip
                        x-small
                        :color="item.stock <= 5 ? '#FFEBEE' : '#E8F5E9'"
                        :text-color="item.stock <= 5 ? '#E53935' : '#2E7D32'"
                      >
                        {{ item.stock }}
                      </v-chip>
                    </td>
                    <td class="hidden-sm-and-down">
                      <span class="shop-name">{{
                        item.seller ? item.seller.shopName : "-"
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Order Status Summary -->
        <v-col cols="12" lg="5">
          <v-card class="card-container pa-0 mb-4" elevation="0">
            <v-card-title class="px-5 pt-5 pb-2">
              <v-icon color="#1565C0" class="mr-2">mdi-chart-pie</v-icon>
              <span class="section-title">สถานะออเดอร์</span>
            </v-card-title>
            <v-card-text class="px-5 pb-5">
              <div
                v-if="Object.keys(orderStatusSummary).length === 0"
                class="text-center py-6"
              >
                <div class="no-data-text">ยังไม่มีข้อมูลออเดอร์</div>
              </div>
              <div v-else>
                <div
                  v-for="(item, idx) in orderStatusList"
                  :key="idx"
                  class="status-row d-flex align-center py-2"
                >
                  <v-avatar :color="item.color" size="10" class="mr-3" />
                  <span class="status-label flex-grow-1">{{
                    item.label
                  }}</span>
                  <span class="status-count font-weight-bold">{{
                    formatNumber(item.count)
                  }}</span>
                  <span class="status-pct ml-2">{{ item.pct }}%</span>
                </div>

                <v-divider class="my-3" />
                <div class="d-flex align-center">
                  <span class="status-label flex-grow-1 font-weight-bold"
                    >ทั้งหมด</span
                  >
                  <span class="status-count font-weight-bold">{{
                    formatNumber(totalOrderCount)
                  }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Today Highlight -->
          <v-card class="card-container pa-0" elevation="0">
            <v-card-title class="px-5 pt-5 pb-2">
              <v-icon color="#F4811F" class="mr-2">mdi-clock-outline</v-icon>
              <span class="section-title">วันนี้</span>
            </v-card-title>
            <v-card-text class="px-5 pb-5">
              <v-row>
                <v-col cols="6">
                  <div class="today-card pa-3 text-center rounded-lg">
                    <div class="today-value">
                      {{ formatNumber(stats.todayOrders || 0) }}
                    </div>
                    <div class="today-label">ออเดอร์</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="today-card pa-3 text-center rounded-lg">
                    <div class="today-value">
                      ฿{{ formatCurrency(stats.todayRevenue || 0) }}
                    </div>
                    <div class="today-label">รายได้</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recent Orders -->
      <v-row class="mt-2">
        <v-col cols="12">
          <v-card class="card-container pa-0" elevation="0">
            <v-card-title class="px-5 pt-5 pb-2">
              <div class="d-flex align-center" style="width: 100%">
                <v-icon color="#1565C0" class="mr-2"
                  >mdi-clipboard-list</v-icon
                >
                <span class="section-title">ออเดอร์ล่าสุด</span>
                <v-spacer />
                <v-btn
                  text
                  small
                  color="primary"
                  :to="{ name: 'admin_orders' }"
                >
                  ดูทั้งหมด
                  <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card-title>

            <v-card-text class="px-5 pb-5">
              <div
                v-if="recentOrders.length === 0"
                class="text-center py-10"
              >
                <img
                  src="@/assets/not_exist_search.svg"
                  alt="no-data"
                  width="80"
                  height="80"
                />
                <div class="no-data-text mt-3">ยังไม่มีออเดอร์</div>
              </div>

              <v-simple-table v-else>
                <thead>
                  <tr>
                    <th>หมายเลขออเดอร์</th>
                    <th>ลูกค้า</th>
                    <th>ร้านค้า</th>
                    <th class="text-center">สถานะ</th>
                    <th class="text-center">การชำระเงิน</th>
                    <th class="text-right">ยอดรวม</th>
                    <th class="hidden-sm-and-down">วันที่</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in recentOrders" :key="order.id">
                    <td class="font-weight-medium">
                      {{ order.orderNumber }}
                    </td>
                    <td>
                      <div class="d-flex align-center">
                        <v-avatar size="28" class="mr-2">
                          <v-img
                            v-if="order.buyer && order.buyer.profileImage"
                            :src="order.buyer.profileImage"
                          />
                          <v-icon v-else small>mdi-account</v-icon>
                        </v-avatar>
                        <span>{{ getBuyerName(order.buyer) }}</span>
                      </div>
                    </td>
                    <td>
                      {{ order.seller ? order.seller.shopName : "-" }}
                    </td>
                    <td class="text-center">
                      <v-chip
                        x-small
                        :color="getStatusColor(order.status).bg"
                        :text-color="getStatusColor(order.status).text"
                      >
                        {{ getStatusLabel(order.status) }}
                      </v-chip>
                    </td>
                    <td class="text-center">
                      <v-chip
                        x-small
                        :color="
                          getPaymentColor(order.paymentStatus).bg
                        "
                        :text-color="
                          getPaymentColor(order.paymentStatus).text
                        "
                      >
                        {{ getPaymentLabel(order.paymentStatus) }}
                      </v-chip>
                    </td>
                    <td class="text-right font-weight-medium">
                      ฿{{ formatCurrency(order.grandTotal) }}
                    </td>
                    <td class="hidden-sm-and-down text-caption">
                      {{ formatDate(order.createdAt) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import {
  getAdminDashboardStatsApi,
  getAdminTopProductsApi,
  getAdminRecentOrdersApi,
  getAdminOrderStatusApi,
} from "@/services/api/adminServices";

const STATUS_MAP = {
  pending: { label: "รอดำเนินการ", color: "#FFA726", bg: "#FFF3E0", text: "#E65100" },
  processing: { label: "กำลังจัดเตรียม", color: "#42A5F5", bg: "#E3F2FD", text: "#1565C0" },
  shipped: { label: "จัดส่งแล้ว", color: "#7E57C2", bg: "#EDE7F6", text: "#4527A0" },
  delivered: { label: "ส่งสำเร็จ", color: "#66BB6A", bg: "#E8F5E9", text: "#2E7D32" },
  cancelled: { label: "ยกเลิก", color: "#EF5350", bg: "#FFEBEE", text: "#C62828" },
  refunded: { label: "คืนเงิน", color: "#EC407A", bg: "#FCE4EC", text: "#AD1457" },
  completed: { label: "สำเร็จ", color: "#26A69A", bg: "#E0F2F1", text: "#00695C" },
};

const PAYMENT_MAP = {
  pending: { label: "รอชำระ", bg: "#FFF3E0", text: "#E65100" },
  paid: { label: "ชำระแล้ว", bg: "#E8F5E9", text: "#2E7D32" },
  failed: { label: "ล้มเหลว", bg: "#FFEBEE", text: "#C62828" },
  refunded: { label: "คืนเงิน", bg: "#FCE4EC", text: "#AD1457" },
};

export default {
  name: "AdminDashboard",

  data() {
    return {
      isLoading: true,
      isRefreshing: false,
      stats: {},
      topProducts: [],
      recentOrders: [],
      orderStatusSummary: {},
    };
  },

  computed: {
    todayLabel() {
      return new Date().toLocaleDateString("th-TH", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    statCards() {
      const s = this.stats;
      return [
        {
          key: "revenue",
          label: "รายได้ทั้งหมด",
          value: `฿${this.formatCurrency(s.totalRevenue || 0)}`,
          icon: "mdi-cash-multiple",
          bgColor: "#E8F5E9",
          iconColor: "#2E7D32",
          sub: `วันนี้ ฿${this.formatCurrency(s.todayRevenue || 0)}`,
        },
        {
          key: "orders",
          label: "ออเดอร์ทั้งหมด",
          value: this.formatNumber(s.totalOrders || 0),
          icon: "mdi-package-variant-closed",
          bgColor: "#E3F2FD",
          iconColor: "#1565C0",
          sub: `วันนี้ ${this.formatNumber(s.todayOrders || 0)} รายการ`,
        },
        {
          key: "users",
          label: "ผู้ใช้งาน",
          value: this.formatNumber(s.totalUsers || 0),
          icon: "mdi-account-group",
          bgColor: "#F3E5F5",
          iconColor: "#7B1FA2",
        },
        {
          key: "sellers",
          label: "ร้านค้า",
          value: this.formatNumber(s.totalSellers || 0),
          icon: "mdi-store",
          bgColor: "#FFF3E0",
          iconColor: "#E65100",
          sub: `เปิดใช้งาน ${this.formatNumber(s.activeSellers || 0)} ร้าน`,
        },
        {
          key: "products",
          label: "สินค้าทั้งหมด",
          value: this.formatNumber(s.totalProducts || 0),
          icon: "mdi-tag-multiple",
          bgColor: "#E0F7FA",
          iconColor: "#00838F",
          sub: `เปิดขาย ${this.formatNumber(s.activeProducts || 0)} รายการ`,
        },
      ];
    },

    totalOrderCount() {
      return Object.values(this.orderStatusSummary).reduce(
        (sum, c) => sum + c,
        0
      );
    },

    orderStatusList() {
      const total = this.totalOrderCount || 1;
      return Object.entries(this.orderStatusSummary).map(([key, count]) => {
        const info = STATUS_MAP[key] || {
          label: key,
          color: "#9E9E9E",
        };
        return {
          key,
          label: info.label,
          color: info.color,
          count,
          pct: ((count / total) * 100).toFixed(1),
        };
      });
    },
  },

  async created() {
    await this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.isLoading = true;
      try {
        const [statsRes, topRes, ordersRes, statusRes] = await Promise.all([
          getAdminDashboardStatsApi().catch(() => null),
          getAdminTopProductsApi(10).catch(() => null),
          getAdminRecentOrdersApi(10).catch(() => null),
          getAdminOrderStatusApi().catch(() => null),
        ]);

        if (statsRes?.stats) this.stats = statsRes.stats;
        if (topRes?.products) this.topProducts = topRes.products;
        if (ordersRes?.orders) this.recentOrders = ordersRes.orders;
        if (statusRes?.summary) this.orderStatusSummary = statusRes.summary;
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async refreshData() {
      this.isRefreshing = true;
      await this.loadDashboard();
      this.isRefreshing = false;
    },

    formatNumber(n) {
      return Number(n || 0).toLocaleString("th-TH");
    },

    formatCurrency(n) {
      return Number(n || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getRankColor(rank) {
      if (rank === 1) return "#FFB300";
      if (rank === 2) return "#90A4AE";
      if (rank === 3) return "#A1887F";
      return "#BDBDBD";
    },

    getStatusColor(status) {
      return STATUS_MAP[status] || { bg: "#F5F5F5", text: "#616161" };
    },

    getStatusLabel(status) {
      return (STATUS_MAP[status] || {}).label || status;
    },

    getPaymentColor(status) {
      return PAYMENT_MAP[status] || { bg: "#F5F5F5", text: "#616161" };
    },

    getPaymentLabel(status) {
      return (PAYMENT_MAP[status] || {}).label || status;
    },

    getBuyerName(buyer) {
      if (!buyer) return "-";
      const name = [buyer.firstName, buyer.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      return name || buyer.username || "-";
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2a39;
}

.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}

.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
  border: 1px solid #f0f1f3;
}

/* Stat cards */
.stat-card {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
  border: 1px solid #f0f1f3;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(16, 24, 40, 0.08) !important;
}

.stat-label {
  font-size: 13px;
  color: #6b717f;
  line-height: 1.3;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2a39;
  line-height: 1.3;
}

.stat-sub {
  font-size: 12px;
  color: #9da3ae;
}

/* Top products table */
.top-product-table th {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #6b717f !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.rank-badge {
  font-size: 12px;
}

.product-name-cell {
  min-width: 0;
  overflow: hidden;
}

.product-name {
  font-size: 13px;
  font-weight: 500;
  color: #1f2a39;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.product-category {
  font-size: 11px;
  color: #9da3ae;
}

.shop-name {
  font-size: 12px;
  color: #6b717f;
}

/* Order status */
.status-row + .status-row {
  border-top: 1px solid #f5f5f5;
}

.status-label {
  font-size: 13px;
  color: #4a5064;
}

.status-count {
  font-size: 14px;
  color: #1f2a39;
}

.status-pct {
  font-size: 12px;
  color: #9da3ae;
  min-width: 42px;
  text-align: right;
}

/* Today highlight */
.today-card {
  background: #fafbfc;
  border: 1px solid #f0f1f3;
}

.today-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2a39;
}

.today-label {
  font-size: 12px;
  color: #9da3ae;
  margin-top: 2px;
}
</style>
